import { ApiError, getToken } from "./api";

export interface ImportProfile {
  code: string;
  label: string;
  fileExtensions: string[];
  targetKind?: string;
}

export interface PreviewItem {
  kind: "INVESTMENT" | "CASHFLOW";
  action: "CREATE" | "SKIP_DUPLICATE" | "CONFLICT";
  label: string;
  details?: string;
}

export interface PreviewResult {
  willCreate: PreviewItem[];
  willSkip: PreviewItem[];
  conflicts: PreviewItem[];
}

export interface CreateSessionResponse {
  sessionId: string;
  summary: { totalRows: number; validRows: number; skippedRows: number };
  preview: PreviewResult;
  warnings: { rowIndex: number; reason: string; raw: string }[];
  hasMoreWarnings: boolean;
}

export interface CommitResult {
  createdCount: number;
  skippedCount: number;
  errors: { rowIndex: number; reason: string }[];
}

export async function listProfiles(): Promise<{ profiles: ImportProfile[] }> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch("/api/import/profiles", { headers });
  if (!res.ok) {
    let code = "UNKNOWN";
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (body.error) code = body.error;
      if (body.message) message = body.message;
    } catch {
      // nicht parsbar — Defaults behalten
    }
    throw new ApiError(res.status, code, message);
  }
  return res.json() as Promise<{ profiles: ImportProfile[] }>;
}

export async function createSession(
  file: File,
  profileCode: string,
): Promise<CreateSessionResponse> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const fd = new FormData();
  fd.append("file", file);
  fd.append("profileCode", profileCode);

  const res = await fetch("/api/import/sessions", {
    method: "POST",
    headers,
    body: fd,
  });

  if (!res.ok) {
    let code = "UNKNOWN";
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (body.error) code = body.error;
      if (body.message) message = body.message;
    } catch {
      // nicht parsbar — Defaults behalten
    }
    throw new ApiError(res.status, code, message);
  }

  return res.json() as Promise<CreateSessionResponse>;
}

export async function commitSession(sessionId: string): Promise<CommitResult> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`/api/import/sessions/${sessionId}/commit`, {
    method: "POST",
    headers,
  });

  if (!res.ok) {
    let code = "UNKNOWN";
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (body.error) code = body.error;
      if (body.message) message = body.message;
    } catch {
      // nicht parsbar — Defaults behalten
    }
    throw new ApiError(res.status, code, message);
  }

  return res.json() as Promise<CommitResult>;
}

export async function cancelSession(sessionId: string): Promise<void> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  await fetch(`/api/import/sessions/${sessionId}`, {
    method: "DELETE",
    headers,
  });
}
